// CreatePermission.jsx
// Excel-style spreadsheet UI — inline editing, grouped by module
// ✅ Naya permission save hone par usePermissions P cache auto-clear hota hai

import React, { useState, useEffect } from 'react';
import {
  Plus, X, Shield, CheckCircle, Loader, Trash2, Edit2, Save, ChevronDown
} from 'lucide-react';
import { permissionsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import { invalidatePermissionsCache } from '../hooks/Usepermissions'; // ✅

// ─── Options — DB se exact match ─────────────────────────────────────────────
const MODULE_OPTIONS = [
  'Lead', 'Lead Detail', 'Lead Data', 'Lead to Order',
  'Orders', 'Order Data', 'Analytics', 'Verified',
  'Dispatch', 'In-Transit', 'OFD', 'Delivered',
  'NDR', 'RTO', 'Billing', 'Products',
  'Employees', 'Floor', 'User Permission',
  'Analytics', 'Attendance', 'Dashboard',
];

const CATEGORY_OPTIONS = [
  'Lead Management', 'Order Management', 'Analytics',
  'Verification', 'Logistics', 'Finance',
  'Inventory', 'HR Management', 'Administration', 'Operations',
];

const ACTION_SUGGESTIONS = [
  'View', 'Create', 'Edit', 'Delete', 'Export', 'Import',
  'Approve', 'Reject', 'Assign', 'Filter', 'Download', 'Upload',
  'Verify', 'Convert', 'Resolve', 'Process', 'Confirm', 'Update', 'Manage',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const generateCode = (module, action) => {
  if (!module || !action) return '';
  return `${module.toUpperCase()}_${action.toUpperCase()}`
    .replace(/\s+/g, '_').replace(/-/g, '_');
};

function StyledSelect({ value, onChange, options, placeholder }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`w-full appearance-none px-3 py-2.5 border rounded-lg text-sm focus:outline-none pr-8 transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
        onFocus={e => e.target.style.borderColor = currentTheme.color}
        onBlur={e => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => <option key={opt} value={opt} className={isDark ? 'bg-[#1e293b]' : ''}>{opt}</option>)}
      </select>
      <ChevronDown className={`w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
function CreatePermission() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.textColor?.toLowerCase() === '#ffffff';

  const [permissions, setPermissions] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [customAction, setCustomAction] = useState(false);
  const [filterModule, setFilterModule] = useState('');
  const [searchCode, setSearchCode] = useState('');

  const EMPTY_FORM = {
    module: '', action: '', category: '',
    code: '', display_name: '', description: '',
    is_active: true, is_critical: false,
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => { loadPermissions(); }, []);

  const loadPermissions = async () => {
    try {
      setLoadingList(true);
      const data = await permissionsApi.getAllPermissions();
      setPermissions(data || []);
    } catch { setPermissions([]); }
    finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (!editingId) set('code', generateCode(form.module, form.action));
  }, [form.module, form.action]);

  useEffect(() => {
    if (!editingId && form.module && form.action)
      set('display_name', `${form.module} - ${form.action}`);
  }, [form.module, form.action]);

  const resetForm = () => { setForm(EMPTY_FORM); setCustomAction(false); setEditingId(null); };

  const startEdit = (perm) => {
    setEditingId(perm.id);
    setForm({
      module:       perm.module || '',
      action:       perm.action || '',
      category:     perm.category || '',
      code:         perm.code || '',
      display_name: perm.display_name ?? perm.displayName ?? '',
      description:  perm.description || '',
      is_active:    perm.is_active !== undefined ? Boolean(perm.is_active) : Boolean(perm.isActive),
      is_critical:  perm.is_critical !== undefined ? Boolean(perm.is_critical) : Boolean(perm.isCritical),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`"${code}" delete karna chahte ho?`)) return;
    try {
      await permissionsApi.deletePermission(id);
      setPermissions(p => p.filter(x => x.id !== id));
      invalidatePermissionsCache(); // ✅ cache clear karo
      message.success('Deleted!');
    } catch { message.error('Delete failed'); }
  };

  const handleToggle = async (id) => {
    try {
      await permissionsApi.togglePermissionStatus(id);
      setPermissions(p => p.map(x =>
        x.id === id ? { ...x, is_active: !x.is_active, isActive: !x.isActive } : x
      ));
    } catch { message.error('Toggle failed'); }
  };

  // ── Submit — payload DB columns se exact match + cache clear ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.module || !form.action || !form.category) {
      message.warning('Module, Action aur Category required hai');
      return;
    }
    if (!form.code) { message.warning('Code empty hai'); return; }
    if (!editingId && permissions.some(p => p.code === form.code)) {
      message.error(`"${form.code}" already exists!`);
      return;
    }

    const payload = {
      module:       form.module,
      action:       form.action,
      category:     form.category,
      code:         form.code,
      display_name: form.display_name || form.code,
      description:  form.description || '',
      is_active:    form.is_active,
      is_critical:  form.is_critical,
    };

    try {
      setSaving(true);
      if (editingId) {
        await permissionsApi.updatePermission(editingId, payload);
        setPermissions(p => p.map(x => x.id === editingId ? { ...x, ...payload } : x));
        message.success('Updated!');
      } else {
        const created = await permissionsApi.createPermission(payload);
        setPermissions(p => [...p, created]);
        message.success(`"${form.code}" created!`);
      }

      // ✅ Cache clear — useAllPermissions next call pe fresh DB se fetch karega
      invalidatePermissionsCache();

      resetForm();
    } catch { message.error(editingId ? 'Update failed' : 'Create failed'); }
    finally { setSaving(false); }
  };

  // ── Filter + Group ──
  const filtered = permissions.filter(p => {
    const modOk  = filterModule ? p.module === filterModule : true;
    const srchOk = searchCode
      ? (p.code?.toLowerCase().includes(searchCode.toLowerCase()) ||
         (p.display_name ?? p.displayName ?? '').toLowerCase().includes(searchCode.toLowerCase()))
      : true;
    return modOk && srchOk;
  });

  const grouped = filtered.reduce((acc, p) => {
    const m = p.module || 'GENERAL';
    (acc[m] = acc[m] || []).push(p);
    return acc;
  }, {});

  const getActive   = (p) => p.is_active   !== undefined ? Boolean(p.is_active)   : Boolean(p.isActive);
  const getCritical = (p) => p.is_critical  !== undefined ? Boolean(p.is_critical) : Boolean(p.isCritical);

  return (
    <div className={`p-6 min-h-screen space-y-6 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : ''}`} style={!isDark ? { backgroundColor: currentTheme.color + '08' } : {}}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Permissions</h1>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
          style={{ backgroundColor: currentTheme.color }}>
          <Shield className="w-4 h-4" />
          <span>{permissions.length} Total</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* ═══════════ LEFT: FORM ═══════════ */}
        <div className="xl:col-span-2">
          <div className={`rounded-2xl border transition-all shadow-sm overflow-hidden ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
            <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-100'}`}
              style={{ background: `linear-gradient(135deg, ${currentTheme.color}15, ${currentTheme.color}05)` }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: currentTheme.color + '20' }}>
                  {editingId
                    ? <Edit2 className="w-4 h-4" style={{ color: currentTheme.color }} />
                    : <Plus  className="w-4 h-4" style={{ color: currentTheme.color }} />}
                </div>
                <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {editingId ? 'Edit Permission' : 'New Permission'}
                </h2>
              </div>
              {editingId && (
                <button onClick={resetForm} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">

              {/* Module */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Module <span className="text-red-400">*</span>
                </label>
                <StyledSelect value={form.module} onChange={v => set('module', v)}
                  options={MODULE_OPTIONS} placeholder="— Module select karo —" />
              </div>

              {/* Action */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Action <span className="text-red-400">*</span>
                </label>
                {!customAction ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {ACTION_SUGGESTIONS.map(action => (
                        <button key={action} type="button" onClick={() => set('action', action)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                          style={{
                            backgroundColor: form.action === action ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.05)' : 'white'),
                            borderColor:     form.action === action ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'),
                            color:           form.action === action ? 'white' : (isDark ? '#94a3b8' : '#374151'),
                          }}>
                          {action}
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setCustomAction(true)}
                      className="text-xs underline" style={{ color: currentTheme.color }}>
                      + Custom action
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" value={form.action}
                      onChange={e => set('action', e.target.value)}
                      placeholder="e.g. Update Status"
                      className={`flex-1 px-3 py-2.5 border rounded-lg text-sm focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
                      onFocus={e => e.target.style.borderColor = currentTheme.color}
                      onBlur={e => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'} />
                    <button type="button"
                      onClick={() => { setCustomAction(false); set('action', ''); }}
                      className={`p-2.5 border rounded-lg transition-colors ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-300 hover:bg-gray-50'}`}>
                      <X className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Code (auto) */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Code <span className="ml-1 normal-case font-normal text-gray-400">(auto-generated)</span>
                </label>
                <div className="flex gap-2 items-center px-3 py-2.5 border rounded-lg text-sm font-mono"
                  style={{ borderColor: currentTheme.color + '50', backgroundColor: currentTheme.color + '08' }}>
                  <span style={{ color: currentTheme.color }} className="font-bold flex-1">{form.code || '—'}</span>
                  {form.code && <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.color }} />}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Category <span className="text-red-400">*</span>
                </label>
                <StyledSelect value={form.category} onChange={v => set('category', v)}
                  options={CATEGORY_OPTIONS} placeholder="— Category select karo —" />
              </div>

              {/* display_name */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Display Name
                  <span className="ml-1 normal-case font-normal text-gray-400">(DB: display_name)</span>
                </label>
                <input type="text" value={form.display_name}
                  onChange={e => set('display_name', e.target.value)}
                  placeholder="e.g. Lead - View"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
                  onFocus={e => e.target.style.borderColor = currentTheme.color}
                  onBlur={e => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'} />
              </div>

              {/* description */}
              <div>
                <label className={`block text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                  placeholder="Permission description..."
                  rows={2}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none resize-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
                  onFocus={e => e.target.style.borderColor = currentTheme.color}
                  onBlur={e => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'} />
              </div>

              {/* is_active + is_critical */}
              <div className="space-y-2">
                <div className={`flex items-center justify-between py-2 px-3 rounded-lg border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                  <div>
                    <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-700'}`}>Active</span>
                    <span className="ml-2 text-xs text-gray-400 font-mono">is_active = {form.is_active ? '1' : '0'}</span>
                  </div>
                  <button type="button" onClick={() => set('is_active', !form.is_active)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors border-2 border-white shadow-sm ${form.is_active ? '' : (isDark ? 'bg-white/10' : 'bg-gray-300')}`}
                    style={{ backgroundColor: form.is_active ? currentTheme.color : undefined }}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className={`flex items-center justify-between py-2 px-3 rounded-lg border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                  <div>
                    <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-700'}`}>Critical</span>
                    <span className="ml-2 text-xs text-gray-400 font-mono">is_critical = {form.is_critical ? '1' : '0'}</span>
                  </div>
                  <button type="button" onClick={() => set('is_critical', !form.is_critical)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors border-2 border-white shadow-sm ${form.is_critical ? '' : (isDark ? 'bg-white/10' : 'bg-gray-300')}`}
                    style={{ backgroundColor: form.is_critical ? '#EF4444' : undefined }}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${form.is_critical ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button type="submit"
                disabled={saving || !form.module || !form.action || !form.category}
                className="w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md border-2 border-white"
                style={{ backgroundColor: currentTheme.color }}>
                {saving
                  ? <><Loader className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                  : editingId
                    ? <><Save className="w-4 h-4" /><span>Update</span></>
                    : <><Plus className="w-4 h-4" /><span>Create Permission</span></>}
              </button>

              {/* Live DB payload preview */}
              {form.code && (
                <div className="p-3 rounded-lg text-xs font-mono bg-gray-900 text-gray-100 border border-gray-700 space-y-0.5">
                  <p className="text-gray-500 mb-1">// DB mein yaise jayega:</p>
                  <p><span className="text-yellow-400">module</span>:       <span className="text-green-300">"{form.module}"</span></p>
                  <p><span className="text-yellow-400">action</span>:       <span className="text-green-300">"{form.action}"</span></p>
                  <p><span className="text-yellow-400">code</span>:         <span className="text-green-300">"{form.code}"</span></p>
                  <p><span className="text-yellow-400">display_name</span>: <span className="text-green-300">"{form.display_name}"</span></p>
                  <p><span className="text-yellow-400">category</span>:     <span className="text-green-300">"{form.category}"</span></p>
                  <p><span className="text-yellow-400">is_active</span>:    <span className="text-blue-300">{form.is_active ? 1 : 0}</span></p>
                  <p><span className="text-yellow-400">is_critical</span>:  <span className="text-blue-300">{form.is_critical ? 1 : 0}</span></p>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* ═══════════ RIGHT: EXCEL TABLE ═══════════ */}
        <div className="xl:col-span-3">
          <div className={`rounded-2xl border transition-all shadow-sm overflow-hidden ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
            <div className={`px-4 py-3 border-b flex items-center gap-3 ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-100'}`}>
              <h2 className={`font-bold whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-800'}`}>All Permissions</h2>
              <input type="text" value={searchCode} onChange={e => setSearchCode(e.target.value)}
                placeholder="Search code / name..."
                className={`flex-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
                onFocus={e => e.target.style.borderColor = currentTheme.color}
                onBlur={e => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'} />
              <div className="w-36 flex-shrink-0">
                <StyledSelect value={filterModule} onChange={setFilterModule}
                  options={MODULE_OPTIONS} placeholder="All Modules" />
              </div>
              {(filterModule || searchCode) && (
                <button onClick={() => { setFilterModule(''); setSearchCode(''); }}
                  className={`p-1.5 border rounded-lg flex-shrink-0 transition-colors ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-300 hover:bg-gray-50'}`}>
                  <X className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              )}
              <span className="text-xs text-gray-400 whitespace-nowrap">{filtered.length} rows</span>
            </div>

            <div className="overflow-auto" style={{ maxHeight: '680px' }}>
              <table className="w-full border-collapse" style={{ minWidth: '860px', fontSize: '12px' }}>
                <thead className="sticky top-0 z-10">
                  <tr style={{ backgroundColor: currentTheme.color }}>
                    {[
                      { label: 'id',           w: 40  },
                      { label: 'module',        w: 110 },
                      { label: 'code',          w: 200 },
                      { label: 'action',        w: 110 },
                      { label: 'display_name',  w: 160 },
                      { label: 'category',      w: 120 },
                      { label: 'is_active',     w: 72  },
                      { label: 'is_critical',   w: 72  },
                      { label: '',              w: 60  },
                    ].map(col => (
                      <th key={col.label}
                        style={{ width: col.w, minWidth: col.w }}
                        className="px-2 py-2 text-left font-semibold text-white border-r border-white/20 whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loadingList ? (
                    <tr>
                      <td colSpan={9} className="py-16 text-center">
                        <Loader className="w-6 h-6 animate-spin mx-auto" style={{ color: currentTheme.color }} />
                      </td>
                    </tr>
                  ) : Object.keys(grouped).length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-16 text-center text-gray-400">
                        <Shield className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                        <p>Koi permission nahi mila</p>
                      </td>
                    </tr>
                  ) : (
                    Object.entries(grouped).map(([mod, perms]) => (
                      <React.Fragment key={mod}>
                        <tr className={isDark ? 'border-white/5' : 'border-gray-100'}>
                          <td colSpan={9}
                            className={`px-3 py-1.5 font-bold border-y transition-all ${isDark ? 'bg-white/5' : ''}`}
                            style={{
                              backgroundColor: !isDark ? currentTheme.color + '12' : undefined,
                              color: currentTheme.color,
                              borderColor: isDark ? 'rgba(255,255,255,0.05)' : currentTheme.color + '25',
                              fontSize: '11px',
                              letterSpacing: '0.04em',
                            }}>
                            📁 {mod}
                            <span className={`ml-2 font-normal text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({perms.length})</span>
                          </td>
                        </tr>

                        {perms.map((perm, idx) => {
                          const active   = getActive(perm);
                          const critical = getCritical(perm);
                          const editing  = editingId === perm.id;
                          return (
                            <tr key={perm.id}
                              className={`border-b transition-colors group ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-blue-50/50'}`}
                              style={{
                                backgroundColor: editing
                                  ? currentTheme.color + '0E'
                                  : isDark ? '#1e293b' : (idx % 2 === 0 ? '#fff' : '#F9FAFB'),
                              }}>
                              <td className={`px-2 py-2 text-center border-r ${isDark ? 'text-gray-500 border-white/5' : 'text-gray-400 border-gray-100'}`}>{perm.id}</td>
                              <td className={`px-2 py-2 border-r truncate max-w-[110px] ${isDark ? 'text-gray-300 border-white/5' : 'text-gray-600 border-gray-100'}`} title={perm.module}>{perm.module}</td>
                              <td className={`px-2 py-2 border-r ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <code className="px-1.5 py-0.5 rounded font-bold"
                                  style={{ backgroundColor: currentTheme.color + '12', color: currentTheme.color, fontFamily: 'monospace', fontSize: '11px' }}>
                                  {perm.code}
                                </code>
                              </td>
                              <td className={`px-2 py-2 border-r ${isDark ? 'text-gray-300 border-white/5' : 'text-gray-700 border-gray-100'}`}>{perm.action}</td>
                              <td className={`px-2 py-2 border-r truncate max-w-[160px] ${isDark ? 'text-gray-400 border-white/5' : 'text-gray-500 border-gray-100'}`} title={perm.display_name ?? perm.displayName}>
                                {perm.display_name ?? perm.displayName}
                              </td>
                              <td className={`px-2 py-2 border-r truncate max-w-[120px] ${isDark ? 'text-gray-400 border-white/5' : 'text-gray-500 border-gray-100'}`} title={perm.category}>{perm.category}</td>

                              {/* is_active */}
                              <td className={`px-2 py-2 border-r text-center ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <button type="button" onClick={() => handleToggle(perm.id)}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors border-2 border-white shadow-sm ${active ? '' : (isDark ? 'bg-white/10' : 'bg-gray-300')}`}
                                  style={{ backgroundColor: active ? currentTheme.color : undefined }}>
                                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${active ? 'translate-x-4' : 'translate-x-0'}`} />
                                </button>
                                <div className={`text-center font-bold mt-0.5 ${active ? 'text-green-500' : 'text-red-400'}`} style={{ fontSize: '10px' }}>
                                  {active ? '1' : '0'}
                                </div>
                              </td>

                              {/* is_critical */}
                              <td className={`px-2 py-2 border-r text-center ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <span className={`inline-block px-2 py-0.5 rounded font-bold ${critical ? (isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600') : (isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-400')}`}
                                  style={{ fontSize: '10px' }}>
                                  {critical ? '1' : '0'}
                                </span>
                              </td>

                              {/* Actions */}
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button type="button" onClick={() => startEdit(perm)}
                                    className="p-1 rounded border border-gray-200 hover:bg-gray-100" title="Edit">
                                    <Edit2 className="w-3 h-3 text-gray-500" />
                                  </button>
                                  <button type="button" onClick={() => handleDelete(perm.id, perm.code)}
                                    className="p-1 rounded border border-red-100 hover:bg-red-50" title="Delete">
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={`px-4 py-2 border-t flex items-center justify-between ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
              <span className="text-xs text-gray-400">{filtered.length} / {permissions.length} permissions</span>
              <span className="text-xs text-gray-400 font-mono">id · module · code · action · display_name · category · is_active · is_critical</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default CreatePermission;